// src/pages/BotUsers/index.jsx
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Grid,
  Alert,
  Switch,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { botService } from "@/services/api";
import LoadingSpinner from "@/components/common/LoadingSpinner";

const BOT_TYPES = {
  LINE: "LINE",
  TELEGRAM: "TELEGRAM",
};

const BotUsers = () => {
  const [bots, setBots] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [selectedBot, setSelectedBot] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [querying, setQuerying] = useState(false);

  // 載入機器人清單
  useEffect(() => {
    const fetchBots = async () => {
      setLoading(true);
      try {
        const data = await botService.getBots();
        // 只顯示啟用通知的機器人
        const filteredBots = data.filter((bot) => bot.notify_enabled !== false);
        setBots(filteredBots);
      } catch (err) {
        setError(err.message || "載入機器人清單失敗");
      } finally {
        setLoading(false);
      }
    };

    fetchBots();
  }, []);

  // 當選擇類型時重置選擇的機器人
  useEffect(() => {
    setSelectedBot("");
  }, [selectedType]);

  // 當選擇機器人類型變更時過濾機器人列表
  const filteredBots = selectedType
    ? bots.filter((bot) => bot.bot_type === selectedType)
    : [];

  // 設定 DataGrid 列配置
  const columns = [
    { field: "user_id", headerName: "用戶 ID", width: 220 },
    { field: "display_name", headerName: "名稱", width: 180 },
    { field: "joined_at", headerName: "加入時間", width: 180 },
    { field: "user_desc", headerName: "用戶描述", width: 200 },
    {
      field: "is_enabled",
      headerName: "是否啟用",
      width: 120,
      renderCell: (params) => {
        const handleStatusChange = async (event) => {
          const newStatus = event.target.checked;
          try {
            if (selectedType === "LINE") {
              await botService.updateLineUserStatus(
                params.row.user_id,
                selectedBot,
                newStatus
              );
            } else {
              await botService.updateTelegramChatStatus(
                params.row.user_id,
                selectedBot,
                newStatus
              );
            }
            // 更新本地状态
            const updatedUsers = users.map((user) =>
              user.user_id === params.row.user_id
                ? { ...user, is_enabled: newStatus }
                : user
            );
            setUsers(updatedUsers);
          } catch (err) {
            setError(err.message || "更新狀態失敗");
          }
        };

        return (
          <Switch
            checked={params.row.is_enabled}
            onChange={handleStatusChange}
            color="primary"
          />
        );
      },
    },
  ];

  // 查詢用戶
  const handleQuery = async () => {
    if (!selectedBot) {
      setError("請選擇機器人");
      return;
    }

    setQuerying(true);
    setError(null);

    try {
      const data =
        selectedType === "LINE"
          ? await botService.getLineUsers(selectedBot)
          : await botService.getTelegramChats(selectedBot);
      setUsers(data);
    } catch (err) {
      setError(err.message || "查詢用戶失敗");
    } finally {
      setQuerying(false);
    }
  };

  // 為每行添加 id 屬性
  const rows = users.map((user, index) => ({
    id: index,
    ...user,
  }));

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        機器人用戶查詢
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 選擇機器人類型 */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel id="bot-type-label">機器人類型</InputLabel>
            <Select
              labelId="bot-type-label"
              id="bot-type"
              value={selectedType}
              label="機器人類型"
              onChange={(e) => setSelectedType(e.target.value)}
            >
              {Object.entries(BOT_TYPES).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* 選擇機器人 */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth disabled={!selectedType}>
            <InputLabel id="bot-label">選擇機器人</InputLabel>
            <Select
              labelId="bot-label"
              id="bot"
              value={selectedBot}
              label="選擇機器人"
              onChange={(e) => setSelectedBot(e.target.value)}
            >
              {filteredBots.map((bot) => (
                <MenuItem key={bot.id} value={bot.bot_callback_id}>
                  {bot.bot_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* 查詢按鈕 */}
        <Grid item xs={12} sm={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleQuery}
            disabled={!selectedBot || querying}
            fullWidth
            sx={{ height: "100%" }}
          >
            {querying ? "查詢中..." : "查詢用戶"}
          </Button>
        </Grid>
      </Grid>

      {/* 用戶列表 */}
      <Box sx={{ height: 400, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          components={{
            Toolbar: GridToolbar,
          }}
          disableSelectionOnClick
          loading={querying}
        />
      </Box>
    </Paper>
  );
};

export default BotUsers;
